import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { isOrderType } from '../../constants/visitTypes';
import { OrderDetailPanel } from '../order/OrderDetailPanel';
import { OutsideEpisodeBadge } from '../episode/OutsideEpisodeBadge';
import {
  ArrowLeft,
  AlertTriangle,
  Ban,
  Loader2,
  CheckCircle,
  AlertCircle,
  Printer,
  ShieldCheck,
  Pencil,
  SendHorizontal,
  StickyNote,
  LockOpen,
  Save,
  Undo2,
  History,
  ClipboardList,
  MapPin,
} from 'lucide-react';

import {
  getBusinessErrorMessage,
  getUserFacingApiMessage,
} from '../../utils/apiErrorDetail';
import {
  getVisit,
  updateVisitData,
  signTask,
  approveTask,
  breakTaskSignatures,
  updateTaskNotes,
  getTaskSignatures,
  submitTaskWithoutSignature,
  returnTaskToClinician,
  getVisitPdf,
  markVisitAsMissed,
  unmarkVisitAsMissed,
  updateMissedVisitData,
} from '../../services/api';
import type {
  ClinicPhysicianResponse,
  PatientResponse,
  VisitResponse,
  TaskSignatureResponse,
  MissedVisitDataCreate,
  MissedVisitDataUpdate,
  OrderResponse,
} from '../../services/api/types.gen';
import {
  MissedVisitForm,
  MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS,
} from './MissedVisitForm';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';
import {
  OASISForm,
  OASISFormData,
  ReadonlyChartView,
  getOasisAutosaveDebounceMs,
} from '../oasis';
import { AutoSavePill } from '../AutoSavePill';
import { SaveDeclinedError } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { SignatureInvalidationDialog } from '../SignatureInvalidationDialog';
import { AttestationDialog } from '../signing/AttestationDialog';
import type { ESignatureCredentialInput } from '../signing/esignatureCredentialApi';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import { WorkLogDialog } from './WorkLogDialog';
import { EvvReadingsDialog } from './EvvReadingsDialog';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import { formatDate, useTaskPermissions } from '../task/TaskDetailPanel';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
  type TaskHeaderSecondaryAction,
} from '../task/TaskHeaderControls';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { usePdfExport, type PdfSource } from '../../hooks/usePdfExport';

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

interface VisitDetailPanelProps {
  visitId: string;
  patientId: string;
  /** Full patient record; forwarded to the OASIS form's embedded allergies module. */
  patient?: PatientResponse | null;
  onBack?: () => void;
  onVisitUpdated?: (visit: VisitResponse) => void;
  physicians?: ClinicPhysicianResponse[];
}

export const VisitDetailPanel: React.FC<VisitDetailPanelProps> = ({
  visitId,
  patientId,
  patient,
  onBack,
  onVisitUpdated,
  physicians = [],
}) => {
  const navigate = useNavigate();
  const user = useStore(state => state.user);
  const { error: showError } = useToast();
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  // Core visit state
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Signature state
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  const [signingLoading, setSigningLoading] = useState(false);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [invalidationLoading, setInvalidationLoading] = useState(false);

  // Signature invalidation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    data: Record<string, unknown>;
    isFormSave: boolean;
  } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [autoSaveChangeToken, setAutoSaveChangeToken] = useState(0);
  /** Bumped on every missed-visit field edit so a finishing save knows whether newer edits are pending. */
  const missedEditSeqRef = useRef(0);
  const savePromiseRef = useRef<{
    resolve: () => void;
    reject: (reason?: unknown) => void;
  } | null>(null);
  const [isUnlockAction, setIsUnlockAction] = useState(false);

  // Task notes state
  const [taskNotes, setTaskNotes] = useState('');
  const [taskNotesLoading, setTaskNotesLoading] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');
  const notesDialogFrameRef = useRef<number | null>(null);
  const markMissedFrameRef = useRef<number | null>(null);
  const historyDialogFrameRef = useRef<number | null>(null);
  const workLogFrameRef = useRef<number | null>(null);
  const evvFrameRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (notesDialogFrameRef.current !== null) {
        cancelAnimationFrame(notesDialogFrameRef.current);
      }
      if (markMissedFrameRef.current !== null) {
        cancelAnimationFrame(markMissedFrameRef.current);
      }
      if (historyDialogFrameRef.current !== null) {
        cancelAnimationFrame(historyDialogFrameRef.current);
      }
      if (workLogFrameRef.current !== null) {
        cancelAnimationFrame(workLogFrameRef.current);
      }
      if (evvFrameRef.current !== null) {
        cancelAnimationFrame(evvFrameRef.current);
      }
    };
  }, []);

  // Transition notes state (gates sign/approve/break actions)
  const [showTransitionNotesModal, setShowTransitionNotesModal] =
    useState(false);
  const [transitionNotes, setTransitionNotes] = useState('');
  const [pendingTransitionAction, setPendingTransitionAction] = useState<
    (() => Promise<void>) | null
  >(null);

  // PDF export
  const { exportPdf, exportingId } = usePdfExport();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const [evvOpen, setEvvOpen] = useState(false);

  // Missed visit state
  const [showMarkMissedDialog, setShowMarkMissedDialog] = useState(false);
  const [markMissedLoading, setMarkMissedLoading] = useState(false);
  const [unmarkMissedLoading, setUnmarkMissedLoading] = useState(false);
  const [missedDataSaving, setMissedDataSaving] = useState(false);

  // --- Derived permission state ---

  const isEditable = visit ? EDITABLE_STATUSES.has(visit.status) : false;
  const isReadonly = !isEditable;
  const isMissedVisit = Boolean(visit?.missed_visit_data);

  // Clear stale save status whenever the form identity changes: another
  // visit, a missed/normal mode flip, or a discard remount via formKey
  useEffect(() => {
    setAutoSaveStatus('idle');
    setAutoSaveChangeToken(0);
  }, [visitId, isMissedVisit, formKey]);

  // Role-based checks: any user with the matching role flag in the visit's
  // clinic can perform the associated actions.
  // NOTE: visit.assigned_clinician_user_id and visit.case_manager_user_id
  // indicate who the visit shows up for in a personal TODO list (not yet
  // implemented). They do NOT gate action permissions — role flags above do.
  const {
    hasClinicianRole,
    hasCaseManagerRole,
    canSign,
    canApprove,
    canBreakSignatures,
    canSubmitWithoutSignature,
    canReturnToClinician,
  } = useTaskPermissions(user, visit, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  const signButtonLabel = useMemo(() => {
    if (!visit) return 'Sign & Submit';
    if (visit.status === 'submitted') return 'Sign & Return for Review';
    if (visit.status === 'pending_clinician_signature')
      return 'Sign & Complete';
    return 'Sign & Submit';
  }, [visit?.status]);

  const editButtonLabel = useMemo(() => {
    if (!visit) return 'Unlock & Edit';
    // Reopening a finished chart is a distinct, more deliberate action.
    if (visit.status === 'completed' || visit.status === 'exported')
      return 'Reopen';
    if (
      visit.status === 'submitted_with_signature' &&
      hasClinicianRole &&
      !hasCaseManagerRole
    )
      return 'Unsubmit & Edit';
    return 'Unlock & Edit';
  }, [visit?.status, hasClinicianRole, hasCaseManagerRole]);

  // --- Data fetching ---

  useEffect(() => {
    const fetchData = async () => {
      if (!visitId) return;

      try {
        setLoading(true);
        setError(null);

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          setTaskNotes(visitData.data.notes ?? '');
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error fetching visit data:', err);
        setError('Failed to load visit details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visitId, onVisitUpdated]);

  // --- OASIS form save (with signature invalidation dialog) ---

  const performSave = async (data: Record<string, unknown>) => {
    if (!visit || !visitId) return;

    setSaveError(null);

    const updatedVisit = await updateVisitData({
      path: { visit_id: visitId },
      body: { visit_data: data },
    });

    if (updatedVisit?.error) {
      const detailMessage = getUserFacingApiMessage(
        updatedVisit,
        'Failed to save visit data'
      );
      const isReadOnlyError = detailMessage.toLowerCase().includes('read-only');

      if (isReadOnlyError) {
        showError(
          'Read-Only',
          'This visit is now in a read-only state. The page will reload to reflect the latest changes.'
        );
        setTimeout(() => window.location.reload(), 3000);
      }

      throw new Error('Failed to save visit data');
    }

    if (updatedVisit?.data) {
      setVisit(updatedVisit.data);
      onVisitUpdated?.(updatedVisit.data);
    }

    setSignatures([]);
  };

  const handleOASISFormSave = async (formSaveData: OASISFormData) => {
    if (!visit || !visitId) return;

    if (signatures.length > 0) {
      setPendingSaveData({ data: formSaveData, isFormSave: true });
      setShowConfirmDialog(true);

      return new Promise<void>((resolve, reject) => {
        savePromiseRef.current = { resolve, reject };
      });
    }

    try {
      await performSave(formSaveData);
    } catch (err) {
      console.error('Error saving OASIS form data:', err);
      setSaveError('Failed to save form data. Please try again.');
      throw err;
    }
  };

  // Stable identities so the memoized OASISForm skips re-rendering its large
  // question tree when this panel re-renders for unrelated state
  const oasisFormSaveRef = useRef(handleOASISFormSave);
  useEffect(() => {
    oasisFormSaveRef.current = handleOASISFormSave;
  });
  const handleOASISFormSaveStable = useCallback(
    (data: OASISFormData) => oasisFormSaveRef.current(data),
    []
  );
  const handleOasisSaveStatusChange = useCallback(
    (status: AutoSaveStatus, changeToken: number) => {
      setAutoSaveStatus(status);
      setAutoSaveChangeToken(changeToken);
    },
    []
  );
  // A medication order created from the embedded meds module is a new task; open it.
  const handleMedicationOrderCreated = useCallback(
    (order: OrderResponse) => {
      navigate(`/patients/${patientId}/visits/${order.id}`);
    },
    [navigate, patientId]
  );
  const handleMissedDirtyChange = useCallback(
    (dirty: boolean, canSave: boolean) => {
      if (dirty) {
        missedEditSeqRef.current += 1;
        if (canSave) {
          // Restart the pill's debounce countdown in step with the form's
          // autosave timer, which also resets on each edit.
          setAutoSaveChangeToken(t => t + 1);
          setAutoSaveStatus('unsaved');
        } else {
          // Dirty but not yet valid to autosave: a quiet "Not saved" rather
          // than a countdown that never fires. The inline field hints say why.
          // Set it even mid-save: any in-flight save is of the prior valid
          // payload, and once it resolves the form is still dirty+invalid, so
          // "Not saved" is the honest end state (avoids a stuck "Saving...").
          setAutoSaveStatus('blocked');
        }
      } else {
        // The form matches its saved data again, e.g. an edit was reverted
        // by hand or a save completed; latched outcomes stay visible
        setAutoSaveStatus(s =>
          s === 'unsaved' || s === 'blocked' ? 'idle' : s
        );
      }
    },
    []
  );

  const handleConfirmSave = async () => {
    if (!pendingSaveData) return;

    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      await performSave(pendingSaveData.data);
      savePromiseRef.current?.resolve();
    } catch (err) {
      console.error('Error saving after confirmation:', err);
      savePromiseRef.current?.reject(err);
    } finally {
      setInvalidationLoading(false);
      setPendingSaveData(null);
      savePromiseRef.current = null;
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);

    if (isUnlockAction) {
      setIsUnlockAction(false);
      return;
    }

    // The sentinel tells the form's save handler this is a decline, not a
    // failure, so it must not latch an error status
    savePromiseRef.current?.reject(new SaveDeclinedError());
    savePromiseRef.current = null;

    if (pendingSaveData?.isFormSave) {
      setFormKey(prev => prev + 1);
    }

    // The declined edits are discarded by the remount above, so the pill
    // returns to its resting state
    setAutoSaveStatus('idle');
    setPendingSaveData(null);
  };

  // --- Unlock from readonly (break signatures via invalidation dialog) ---

  const handleReturnToEditMode = () => {
    if (!visit) return;
    // Locked visits with no active signatures still need unlock — the backend
    // transitions status even when there is nothing left to invalidate.
    if (signatures.length === 0) {
      void handleUnlockConfirm();
      return;
    }
    setIsUnlockAction(true);
    setShowConfirmDialog(true);
  };

  const handleUnlockConfirm = async () => {
    if (!visit) return;
    setShowConfirmDialog(false);
    setInvalidationLoading(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: visit.id },
      });
      if (result.error) {
        console.error('Error unlocking visit:', result.error);
        setSaveError('Failed to unlock visit. Please try again.');
        return;
      }
      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visit.id } }),
        getTaskSignatures({ path: { task_id: visit.id } }),
      ]);
      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
    } catch (err) {
      console.error('Error unlocking visit:', err);
      setSaveError('Failed to unlock visit. Please try again.');
    } finally {
      setInvalidationLoading(false);
      setIsUnlockAction(false);
    }
  };

  // --- Transition notes pattern (gates sign/approve/break through notes modal) ---

  const executeWithTransitionNotes = (action: () => Promise<void>) => {
    setPendingTransitionAction(() => action);
    setTransitionNotes(taskNotes);
    setShowTransitionNotesModal(true);
  };

  const handleConfirmTransition = async () => {
    if (!pendingTransitionAction || !visitId) return;

    if (transitionNotes !== taskNotes) {
      try {
        await updateTaskNotes({
          path: { task_id: visitId },
          body: { notes: transitionNotes || null },
        });
        setTaskNotes(transitionNotes);
      } catch (err) {
        console.error('Error saving notes:', err);
        setSaveError('Failed to save task notes.');
        setShowTransitionNotesModal(false);
        setPendingTransitionAction(null);
        return;
      }
    }

    setShowTransitionNotesModal(false);
    await pendingTransitionAction();
    setPendingTransitionAction(null);
  };

  const handleCancelTransition = () => {
    setShowTransitionNotesModal(false);
    setPendingTransitionAction(null);
  };

  // --- Sign & Submit ---

  const visitAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('visit', null, { isMissedVisit })
    );
  }, [attestations, isMissedVisit]);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);

  // Returns a message when the signature fails so the attestation dialog, which
  // stays open on failure, can show it. saveError is still set for the case where
  // the clinician closes the dialog and expects to see what went wrong.
  const doSignAndSubmit = async (
    credential: ESignatureCredentialInput
  ): Promise<string | null> => {
    if (!visitId || !visitAttestation) return null;

    try {
      setSigningLoading(true);
      setSaveError(null);

      const signResult = await signTask({
        path: { task_id: visitId },
        body: { ...credential, attestation_version: visitAttestation.version },
      });

      if (signResult.error) {
        console.error('Error signing visit:', signResult.error);
        const message =
          getBusinessErrorMessage(signResult) ??
          'Failed to sign and submit. Please try again.';
        setSaveError(message);
        return message;
      }

      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visitId } }),
        getTaskSignatures({
          path: { task_id: visitId },
        }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
      setSignAttestationOpen(false);
      return null;
    } catch (err) {
      console.error('Error signing visit:', err);
      const message = 'Failed to sign and submit. Please try again.';
      setSaveError(message);
      return message;
    } finally {
      setSigningLoading(false);
    }
  };

  const handleSignAndSubmit = () => {
    setSignAttestationOpen(true);
  };

  // --- Sign & Complete (approve) ---

  const doApprove = async (
    credential: ESignatureCredentialInput
  ): Promise<string | null> => {
    if (!visitId || !visitAttestation) return null;

    try {
      setApprovingLoading(true);
      setSaveError(null);

      const result = await approveTask({
        path: { task_id: visitId },
        body: { ...credential, attestation_version: visitAttestation.version },
      });

      if (result.error) {
        console.error('Error completing visit:', result.error);
        const message =
          getBusinessErrorMessage(result) ??
          'Failed to complete visit. Please try again.';
        setSaveError(message);
        return message;
      }

      const [visitData, signaturesData] = await Promise.all([
        getVisit({ path: { visit_id: visitId } }),
        getTaskSignatures({
          path: { task_id: visitId },
        }),
      ]);

      if (visitData?.data) {
        setVisit(visitData.data);
        onVisitUpdated?.(visitData.data);
      }
      if (signaturesData?.data) {
        setSignatures(signaturesData.data);
      }
      setApproveAttestationOpen(false);
      return null;
    } catch (err) {
      console.error('Error completing visit:', err);
      const message = 'Failed to complete visit. Please try again.';
      setSaveError(message);
      return message;
    } finally {
      setApprovingLoading(false);
    }
  };

  const handleApprove = () => {
    // Transition notes are saved in handleConfirmTransition before this
    // callback runs; doApprove only needs the attestation dialog (no notes).
    executeWithTransitionNotes(async () => {
      setApproveAttestationOpen(true);
    });
  };

  // --- Advance without signing (ROG-3268 deferrals) ---

  const [unsignedMoveLoading, setUnsignedMoveLoading] = useState(false);

  const refreshAfterUnsignedMove = async () => {
    if (!visitId) return;
    const [visitData, signaturesData] = await Promise.all([
      getVisit({ path: { visit_id: visitId } }),
      getTaskSignatures({ path: { task_id: visitId } }),
    ]);
    if (visitData?.data) {
      setVisit(visitData.data);
      onVisitUpdated?.(visitData.data);
    }
    if (signaturesData?.data) {
      setSignatures(signaturesData.data);
    }
  };

  const handleSubmitWithoutSignature = async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await submitTaskWithoutSignature({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error submitting without signature:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to submit. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error submitting without signature:', err);
      setSaveError('Failed to submit. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  };

  const handleReturnToClinician = async () => {
    if (!visitId) return;
    try {
      setUnsignedMoveLoading(true);
      setSaveError(null);
      const result = await returnTaskToClinician({
        path: { task_id: visitId },
      });
      if (result.error) {
        console.error('Error returning to clinician:', result.error);
        setSaveError(
          getBusinessErrorMessage(result) ??
            'Failed to return the chart. Please try again.'
        );
        return;
      }
      await refreshAfterUnsignedMove();
    } catch (err) {
      console.error('Error returning to clinician:', err);
      setSaveError('Failed to return the chart. Please try again.');
    } finally {
      setUnsignedMoveLoading(false);
    }
  };

  // --- Break signatures from header (non-readonly, goes through transition notes) ---

  const handleBreakSignatures = async () => {
    if (!visitId) return;

    const doBreak = async () => {
      try {
        setEditingLoading(true);
        setSaveError(null);

        const result = await breakTaskSignatures({
          path: { task_id: visitId },
        });

        if (result.error) {
          console.error('Error breaking signatures:', result.error);
          setSaveError('Failed to break signatures. Please try again.');
          return;
        }

        const [visitData, signaturesData] = await Promise.all([
          getVisit({ path: { visit_id: visitId } }),
          getTaskSignatures({
            path: { task_id: visitId },
          }),
        ]);

        if (visitData?.data) {
          setVisit(visitData.data);
          onVisitUpdated?.(visitData.data);
        }
        if (signaturesData?.data) {
          setSignatures(signaturesData.data);
        }
      } catch (err) {
        console.error('Error breaking signatures:', err);
        setSaveError('Failed to break signatures. Please try again.');
      } finally {
        setEditingLoading(false);
      }
    };

    executeWithTransitionNotes(doBreak);
  };

  // --- Missed visit actions ---

  const handleMarkAsMissed = async (payload: MissedVisitDataCreate) => {
    if (!visitId) return;
    try {
      setMarkMissedLoading(true);
      setSaveError(null);
      const result = await markVisitAsMissed({
        path: { visit_id: visitId },
        body: payload,
      });
      if (result.error) {
        console.error('Error marking visit as missed:', result.error);
        showError('Unable to mark visit as missed. Please try again.');
        return;
      }
      if (result.data) {
        setVisit(result.data);
        setSignatures([]);
        onVisitUpdated?.(result.data);
      }
      setShowMarkMissedDialog(false);
    } catch (err) {
      console.error('Error marking visit as missed:', err);
      showError('Unable to mark visit as missed. Please try again.');
    } finally {
      setMarkMissedLoading(false);
    }
  };

  const handleUnmarkMissed = async () => {
    if (!visitId) return;
    try {
      setUnmarkMissedLoading(true);
      setSaveError(null);
      const result = await unmarkVisitAsMissed({
        path: { visit_id: visitId },
      });
      if (result.error) {
        console.error('Error unmarking visit:', result.error);
        showError('Unable to unmark visit. Please try again.');
        return;
      }
      if (result.data) {
        setVisit(result.data);
        setSignatures([]);
        onVisitUpdated?.(result.data);
      }
    } catch (err) {
      console.error('Error unmarking visit:', err);
      showError('Unable to unmark visit. Please try again.');
    } finally {
      setUnmarkMissedLoading(false);
    }
  };

  const handleUpdateMissedData = async (payload: MissedVisitDataUpdate) => {
    if (!visitId) return;
    const seqAtStart = missedEditSeqRef.current;
    try {
      setMissedDataSaving(true);
      setSaveError(null);
      setAutoSaveStatus('saving');
      const result = await updateMissedVisitData({
        path: { visit_id: visitId },
        body: payload,
      });
      if (result.error) {
        console.error('Error updating missed visit data:', result.error);
        showError('Unable to save missed visit details. Please try again.');
        setAutoSaveStatus('error');
        return;
      }
      if (result.data) {
        setVisit(result.data);
        onVisitUpdated?.(result.data);
      }
      // Keep showing "unsaved" if the user edited again while this save ran
      if (missedEditSeqRef.current === seqAtStart) {
        setAutoSaveStatus('saved');
      }
    } catch (err) {
      console.error('Error updating missed visit data:', err);
      showError('Unable to save missed visit details. Please try again.');
      setAutoSaveStatus('error');
    } finally {
      setMissedDataSaving(false);
    }
  };

  const handleExportPdf = () => {
    if (!visitId) return;
    void exportPdf(
      visitId,
      async (): Promise<PdfSource | null> => {
        const response = await getVisitPdf({
          path: { visit_id: visitId },
        });
        return response.error || !response.data
          ? null
          : {
              blob: new Blob([response.data as BlobPart], {
                type: 'application/pdf',
              }),
            };
      },
      'visit PDF'
    );
  };

  // --- Render ---

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading visit details...</p>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Visit</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Visit not found'}
          </p>
          <Button
            onClick={() =>
              onBack ? onBack() : navigate(`/patients/${patientId}`)
            }
            variant='outline'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
        </div>
      </div>
    );
  }

  // Delegate to OrderDetailPanel for order visit types
  if (isOrderType(visit.visit_type)) {
    return (
      <OrderDetailPanel
        orderId={visit.id}
        patientId={patientId}
        onBack={onBack}
      />
    );
  }

  const hasPrimaryHeaderActions =
    (canBreakSignatures && !isReadonly) || canSign || canApprove;

  // OASIS has no inline-validation "Not saved": its only refused save is the
  // user cancelling the invalidation dialog, which the modal and the form
  // remount already communicate — so never surface 'blocked' for it (it would
  // otherwise race the cancel path's reset to a sticky "Not saved"). The missed
  // visit form does want it: a dirty-but-incomplete edit shows "Not saved"
  // rather than a save countdown that can't fire. And while the dialog waits
  // for a decision the parked onSave makes the form report "saving" though no
  // request is running, so stay quiet (idle) and let the modal own the moment
  // rather than showing a "Saving…" that isn't true.
  const pillStatus: AutoSaveStatus =
    (autoSaveStatus === 'blocked' && !isMissedVisit) ||
    (showConfirmDialog &&
      pendingSaveData?.isFormSave &&
      autoSaveStatus === 'saving')
      ? 'idle'
      : autoSaveStatus;

  const secondaryHeaderActions: TaskHeaderSecondaryAction[] = [
    ...(isEditable && !isMissedVisit
      ? [
          {
            label: 'Mark as Missed',
            icon: <Ban className='h-4 w-4 mr-2' aria-hidden='true' />,
            // Defer opening to the next frame so the Actions dropdown finishes
            // unmounting (and releases its body pointer-events lock) before the
            // dialog mounts — otherwise the overlapping modals can leave the
            // page unclickable. Mirrors the Notes action below.
            onSelect: () => {
              if (markMissedFrameRef.current !== null) {
                cancelAnimationFrame(markMissedFrameRef.current);
              }
              markMissedFrameRef.current = requestAnimationFrame(() => {
                markMissedFrameRef.current = null;
                setShowMarkMissedDialog(true);
              });
            },
          },
        ]
      : []),
    ...(isEditable && isMissedVisit
      ? [
          {
            label: unmarkMissedLoading ? 'Unmarking…' : 'Unmark as Missed',
            icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
            onSelect: handleUnmarkMissed,
            disabled: unmarkMissedLoading,
          },
        ]
      : []),
    ...(canSubmitWithoutSignature
      ? [
          {
            label: unsignedMoveLoading
              ? 'Submitting…'
              : 'Submit without signing',
            icon: (
              <SendHorizontal className='h-4 w-4 mr-2' aria-hidden='true' />
            ),
            onSelect: () => void handleSubmitWithoutSignature(),
            disabled: unsignedMoveLoading,
          },
        ]
      : []),
    ...(canReturnToClinician
      ? [
          {
            label: unsignedMoveLoading ? 'Returning…' : 'Return to clinician',
            icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
            onSelect: () => void handleReturnToClinician(),
            disabled: unsignedMoveLoading,
          },
        ]
      : []),
    {
      label: 'Notes',
      icon: <StickyNote className='h-4 w-4 mr-2' aria-hidden='true' />,
      onSelect: () => {
        setDraftNotes(taskNotes);
        setSaveError(null);
        if (notesDialogFrameRef.current !== null) {
          cancelAnimationFrame(notesDialogFrameRef.current);
        }
        notesDialogFrameRef.current = requestAnimationFrame(() => {
          notesDialogFrameRef.current = null;
          setShowNotesDialog(true);
        });
      },
    },
    {
      label: exportingId === visitId ? 'Opening print view...' : 'Print view',
      icon:
        exportingId === visitId ? (
          <div
            className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
            aria-hidden='true'
          />
        ) : (
          <Printer className='h-4 w-4 mr-2' aria-hidden='true' />
        ),
      onSelect: handleExportPdf,
      disabled: exportingId === visitId,
    },
    {
      label: 'View history',
      // Defer opening to the next frame so the Actions dropdown finishes
      // unmounting (and releases its body pointer-events lock) before the
      // dialog mounts — otherwise the page is left unclickable. Mirrors the
      // Notes / Mark as Missed actions above.
      icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
      onSelect: () => {
        if (historyDialogFrameRef.current !== null) {
          cancelAnimationFrame(historyDialogFrameRef.current);
        }
        historyDialogFrameRef.current = requestAnimationFrame(() => {
          historyDialogFrameRef.current = null;
          setHistoryOpen(true);
        });
      },
    },
    {
      label: 'Work log',
      // Defer opening to the next frame for the same reason as View history.
      icon: <ClipboardList className='h-4 w-4 mr-2' aria-hidden='true' />,
      onSelect: () => {
        if (workLogFrameRef.current !== null) {
          cancelAnimationFrame(workLogFrameRef.current);
        }
        workLogFrameRef.current = requestAnimationFrame(() => {
          workLogFrameRef.current = null;
          setWorkLogOpen(true);
        });
      },
    },
    {
      // EMR-325: read-only proof-of-visit captured in the nursing app. Always
      // offered — the dialog itself shows an empty state when nothing was
      // captured, which is most visits today.
      label: 'EVV readings',
      // Defer opening to the next frame for the same reason as View history.
      icon: <MapPin className='h-4 w-4 mr-2' aria-hidden='true' />,
      onSelect: () => {
        if (evvFrameRef.current !== null) {
          cancelAnimationFrame(evvFrameRef.current);
        }
        evvFrameRef.current = requestAnimationFrame(() => {
          evvFrameRef.current = null;
          setEvvOpen(true);
        });
      },
    },
  ];

  return (
    <div className='flex flex-col h-full'>
      {/* Visit Header Bar */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <Button
            variant='outline'
            size='sm'
            onClick={() =>
              onBack ? onBack() : navigate(`/patients/${patientId}`)
            }
            className='flex items-center gap-1.5 flex-shrink-0'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            See All Visits
          </Button>
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center gap-2'>
              <span className='min-w-0 truncate text-sm font-medium text-foreground'>
                {visit.visit_type_display_name}
              </span>
              {isMissedVisit && (
                <Badge
                  variant='destructive'
                  className='shrink-0 text-[10px] px-1.5 py-0'
                >
                  Missed
                </Badge>
              )}
              <OutsideEpisodeBadge
                episodeStartDate={visit.episode_start_date}
                episodeEndDate={visit.episode_end_date}
                targetDate={visit.scheduled_date}
                visitDate={visit.visit_date}
              />
            </div>
            <div className='text-xs text-muted-foreground'>
              Visit date: {formatDate(visit.visit_date)} · Last updated:{' '}
              {visit.visit_data_last_updated
                ? formatDateTime(visit.visit_data_last_updated)
                : 'Never'}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <TaskWorkflowStatusBadge
              status={visit.status}
              signatures={signatures}
            />
          }
          primaryActions={
            hasPrimaryHeaderActions ? (
              <>
                {/* Break Signatures Button (header, only for editable states with signatures) */}
                {canBreakSignatures && !isReadonly && (
                  <Button
                    onClick={handleBreakSignatures}
                    disabled={editingLoading}
                    size='sm'
                    variant='outline'
                    className='flex items-center gap-1.5'
                  >
                    {editingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Pencil className='h-4 w-4' />
                        {editButtonLabel}
                      </>
                    )}
                  </Button>
                )}

                {/* Sign Button */}
                {canSign && (
                  <Button
                    onClick={handleSignAndSubmit}
                    disabled={
                      signingLoading || attestationsLoading || !visitAttestation
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {signingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Signing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className='h-4 w-4' />
                        {signButtonLabel}
                      </>
                    )}
                  </Button>
                )}

                {/* Sign & Complete Button */}
                {canApprove && (
                  <Button
                    onClick={handleApprove}
                    disabled={
                      approvingLoading ||
                      attestationsLoading ||
                      !visitAttestation
                    }
                    size='sm'
                    variant='default'
                    className='flex items-center gap-1.5'
                  >
                    {approvingLoading ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                        Completing...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className='h-4 w-4' />
                        Sign & Complete
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : undefined
          }
          secondaryActions={secondaryHeaderActions}
        />
      </div>

      {/* Save status pill: a sibling above the scrollable content div, so it
          stays in view in either scroll configuration — when the content div
          below is the scroller the pill sits outside it, and when the panel
          grows and the page scrolls (Layout's main) the sticky positioning
          pins it to the top. The row collapses while the pill is hidden so
          idle missed-visit forms don't get an empty strip. */}
      {!isReadonly && (
        <div
          className={`pointer-events-none sticky top-0 z-20 flex shrink-0 justify-end px-5 ${
            isMissedVisit && pillStatus === 'idle' ? '' : 'py-2'
          }`}
        >
          <AutoSavePill
            status={pillStatus}
            variant='autosave'
            debounceMs={
              isMissedVisit
                ? MISSED_VISIT_AUTOSAVE_DEBOUNCE_MS
                : getOasisAutosaveDebounceMs(signatures.length > 0)
            }
            changeToken={autoSaveChangeToken}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className='flex-1 overflow-auto p-5 space-y-4'>
        {saveError && (
          <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
            {saveError}
          </div>
        )}

        {/* Signature Invalidation Warning (editable states with active signatures) */}
        {isEditable && signatures.length > 0 && (
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <h4 className='font-semibold text-amber-900 mb-1'>
                  Signature Notice
                </h4>
                <p className='text-sm text-amber-800'>
                  This visit has been signed by {signatures.length}{' '}
                  {signatures.length === 1 ? 'person' : 'people'}. Editing the
                  visit data will invalidate all signatures and require
                  re-signing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Visit Content: Readonly or Editable */}
        {isReadonly ? (
          <div className='space-y-4'>
            <div className='rounded-lg border bg-muted/40 p-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <p className='text-sm font-medium'>
                    {visit.status === 'completed'
                      ? 'Completed Chart (read-only)'
                      : visit.status === 'exported'
                        ? 'Exported Chart (read-only)'
                        : 'Signed Chart (read-only)'}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {visit.status === 'completed'
                      ? 'This visit has been approved and completed.'
                      : visit.status === 'exported'
                        ? 'This visit has been exported.'
                        : 'This visit is locked. Select Edit to remove signatures and return to the editable form.'}
                  </p>
                </div>
                {canBreakSignatures && (
                  <Button
                    size='default'
                    variant='default'
                    onClick={handleReturnToEditMode}
                    disabled={invalidationLoading}
                    className='flex items-center gap-1.5 shrink-0 font-medium shadow-sm'
                  >
                    <LockOpen className='h-4 w-4' />
                    {editButtonLabel}
                  </Button>
                )}
              </div>
            </div>
            {isMissedVisit ? (
              <MissedVisitForm
                key={visit.missed_visit_data?.id ?? 'readonly'}
                data={visit.missed_visit_data}
                isReadonly
                isSaving={false}
                onUpdate={handleUpdateMissedData}
                physicians={physicians}
              />
            ) : (
              <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
                <ReadonlyChartView visitId={visitId} />
              </div>
            )}
          </div>
        ) : isMissedVisit ? (
          <MissedVisitForm
            key={visit.missed_visit_data?.id ?? 'edit'}
            data={visit.missed_visit_data}
            isReadonly={false}
            isSaving={missedDataSaving}
            autosave
            saveErrored={autoSaveStatus === 'error'}
            onUpdate={handleUpdateMissedData}
            physicians={physicians}
            onDirtyChange={handleMissedDirtyChange}
          />
        ) : (
          // Hold the dense 12-column form at a usable width. Below this the
          // surrounding overflow-auto container shows a horizontal scrollbar
          // instead of letting the fields shrink until they overlap. Below
          // `md` the form stacks to a single column, so no width floor.
          <div className='md:min-w-[64rem]'>
            <OASISForm
              key={`${visitId}-${formKey}`}
              visitId={visitId}
              visitType={visit.visit_type || 'SOC'}
              isOasis={visit.is_oasis}
              initialData={visit.visit_data as OASISFormData}
              onSave={handleOASISFormSaveStable}
              hasActiveSignatures={signatures.length > 0}
              showInlineValidation
              onSaveStatusChange={handleOasisSaveStatusChange}
              patientId={patientId}
              patient={patient}
              visitStatus={visit.status}
              episodeId={visit.episode_id}
              episodeStartDate={visit.episode_start_date}
              episodeEndDate={visit.episode_end_date}
              onMedicationOrderCreated={handleMedicationOrderCreated}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={visit.id}
      />

      <WorkLogDialog
        open={workLogOpen}
        onOpenChange={setWorkLogOpen}
        visitId={visit.id}
      />

      <EvvReadingsDialog
        open={evvOpen}
        onOpenChange={setEvvOpen}
        visitId={visit.id}
      />

      <SignatureInvalidationDialog
        open={showConfirmDialog}
        onConfirm={isUnlockAction ? handleUnlockConfirm : handleConfirmSave}
        onCancel={handleCancelSave}
        signatures={signatures}
        isLoading={invalidationLoading}
        actionLabel={isUnlockAction ? editButtonLabel : undefined}
      />

      <AttestationDialog
        open={signAttestationOpen}
        attestation={visitAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel='Sign & Submit'
        onConfirm={doSignAndSubmit}
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        attestation={visitAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approvingLoading}
        actionLabel='Approve & Complete'
        onConfirm={doApprove}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      {/* Task Notes Dialog */}
      <Dialog
        open={showNotesDialog}
        onOpenChange={open => {
          if (!open) setShowNotesDialog(false);
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Task Notes</DialogTitle>
            <DialogDescription>
              Notes are visible to everyone who handles this chart.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={draftNotes}
            onChange={e => setDraftNotes(e.target.value)}
            placeholder='Add notes about this chart...'
            className='min-h-[150px]'
            autoFocus
          />
          {saveError && (
            <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
              {saveError}
            </div>
          )}
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setShowNotesDialog(false)}
              disabled={taskNotesLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!visitId) return;
                if (draftNotes === taskNotes) {
                  setShowNotesDialog(false);
                  return;
                }
                try {
                  setTaskNotesLoading(true);
                  setSaveError(null);
                  const res = await updateTaskNotes({
                    path: { task_id: visitId },
                    body: { notes: draftNotes || null },
                  });
                  setTaskNotes(draftNotes);
                  // Reflect the backend body-edit auto-advance (Not Started ->
                  // Saved) without refetching the whole chart.
                  const nextStatus = res.data?.status;
                  if (nextStatus) {
                    setVisit(prev =>
                      prev ? { ...prev, status: nextStatus } : prev
                    );
                  }
                  setShowNotesDialog(false);
                } catch (err) {
                  console.error('Error saving task notes:', err);
                  setSaveError('Failed to save task notes.');
                } finally {
                  setTaskNotesLoading(false);
                }
              }}
              disabled={taskNotesLoading}
            >
              {taskNotesLoading ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' />
                  Save Notes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Missed Dialog */}
      <Dialog
        open={showMarkMissedDialog}
        onOpenChange={open => {
          if (!open && !markMissedLoading) setShowMarkMissedDialog(false);
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Mark Visit as Missed</DialogTitle>
            <DialogDescription>
              Complete the fields below to record this as a missed visit. The
              visit will be marked non-billable.
            </DialogDescription>
          </DialogHeader>
          <MissedVisitForm
            data={null}
            isReadonly={false}
            isSaving={markMissedLoading}
            onSave={handleMarkAsMissed}
            physicians={physicians}
          />
        </DialogContent>
      </Dialog>

      {/* Transition Notes Modal */}
      <Dialog
        open={showTransitionNotesModal}
        onOpenChange={open => {
          if (!open) handleCancelTransition();
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Task Notes</DialogTitle>
            <DialogDescription>
              Update the task notes before proceeding. These notes are visible
              to everyone who handles this chart.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={transitionNotes}
            onChange={e => setTransitionNotes(e.target.value)}
            placeholder='Add a note about this action...'
            className='min-h-[150px]'
            autoFocus
          />
          <DialogFooter>
            <Button variant='outline' onClick={handleCancelTransition}>
              Cancel
            </Button>
            <Button onClick={handleConfirmTransition}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
